import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

export type Story = {
  id: string;
  title: string;
  people: string | null;
  places: string | null;
  steps: string[];
  link_url: string | null;
  link_label: string | null;
};

export const StoryChain = ({ story }: { story: Story }) => (
  <article className="rounded-xl border border-border/60 bg-card p-7 h-full flex flex-col">
    <h3 className="text-xl font-heading font-bold mb-2 leading-snug">{story.title}</h3>
    {(story.people || story.places) && (
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-5">
        {[story.people, story.places].filter(Boolean).join(" · ")}
      </p>
    )}
    <ol className="space-y-3 flex-1">
      {story.steps.map((step, i) => (
        <li key={i} className="flex gap-3">
          <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" aria-hidden="true" />
          <span className="text-muted-foreground leading-relaxed">{step}</span>
        </li>
      ))}
    </ol>
    {story.link_url && (
      <a
        href={story.link_url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 mt-6 text-sm font-medium text-coral-text hover:text-coral transition-colors"
      >
        {story.link_label?.trim() || "Read more"} →
      </a>
    )}
  </article>
);

const EvidenceSection = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    supabase
      .from("mycelium_stories")
      .select("id, title, people, places, steps, link_url, link_label")
      .eq("published", true)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false })
      .limit(3)
      .then(({ data, error }) => {
        if (!alive) return;
        if (error) console.error("Failed to load stories:", error);
        setStories((data as Story[]) ?? []);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section id="evidence" className="py-24 md:py-32 bg-muted/40">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-14">
          <p className="text-sm uppercase tracking-[0.2em] text-coral-text mb-4">The evidence</p>
          <h2 className="text-3xl md:text-5xl font-heading font-bold mb-6">
            Relationships becoming{" "}
            <span className="text-gradient-coral">possibilities.</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Not testimonials. Chains. Someone met someone, a question appeared, and something existed that had not
            existed before.
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-56 rounded-xl border border-border/50 bg-card animate-pulse" />
            ))}
          </div>
        ) : stories.length === 0 ? (
          <p className="text-center text-muted-foreground">
            The first stories are being gathered right now.
          </p>
        ) : (
          <>
            <div className="grid md:grid-cols-3 gap-6">
              {stories.map((story) => (
                <StoryChain key={story.id} story={story} />
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/stories"
                className="inline-flex items-center gap-2 min-h-[44px] text-coral-text hover:text-coral font-medium transition-colors"
              >
                See more stories →
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default EvidenceSection;